import type { ReactNode } from "react";
import { Package, RotateCcw } from "@/components/icons";
import { Badge, Button } from "@/components/kit";
import { cn } from "@/lib/utils";
import type {
  GoodsReceiptListItemDTO,
  ImportOrderPhase13DetailDTO,
  ImportOrderPhase13LineDTO,
  ImportOrderReceiptDTO,
  ImportOrderTrackingRowDTO,
} from "@/modules/inventory/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  formatLakAmount,
  IMPORT_ORDER_CURRENCY_LABELS,
  IMPORT_ORDER_SOURCE_CLASS,
  IMPORT_ORDER_SOURCE_LABELS,
  IMPORT_ORDER_STATUS_CLASS,
  IMPORT_ORDER_STATUS_LABELS,
} from "../lib/import-order-labels";
import { ImportOrderReceiveProgress } from "./ImportOrderReceiveProgress";
import { ImportOrderPendingCountBadge } from "./ImportOrderPendingBadge";

function productGroupsFromLines(lines: ImportOrderPhase13LineDTO[]): string[] {
  const seen = new Set<string>();
  const groups: string[] = [];
  for (const line of lines) {
    const name = line.productName.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    groups.push(name);
  }
  return groups;
}

function DetailSection({
  title,
  hint,
  children,
  className,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("brand-card overflow-hidden", className)}>
      <div className="flex items-center justify-between gap-2 border-secondary/15 border-b px-4 py-3">
        <h2 className="font-display text-lg">{title}</h2>
        {hint ? (
          <span className="text-muted-foreground text-xs tabular-nums">{hint}</span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function MetaField({
  label,
  value,
  warn,
}: {
  label: string;
  value: ReactNode;
  warn?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/25 px-3 py-2.5">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 font-medium text-sm leading-snug",
          warn && "text-destructive",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "secondary" | "leaf" | "warn";
}) {
  return (
    <div
      className={cn(
        "rounded-xl border px-3 py-3",
        accent === "secondary" && "border-secondary/25 bg-secondary/5",
        accent === "leaf" && "border-leaf/25 bg-leaf/5",
        accent === "warn" && "border-destructive/20 bg-destructive/5",
        !accent && "border-border/60 bg-background/60",
      )}
    >
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 font-display text-xl tabular-nums leading-none",
          accent === "secondary" && "text-secondary",
          accent === "leaf" && "text-leaf",
          accent === "warn" && "text-destructive",
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function ImportOrderDetailHero({
  order,
}: {
  order: ImportOrderPhase13DetailDTO;
}) {
  const productGroups = productGroupsFromLines(order.lines);

  return (
    <article
      className={cn(
        "brand-card overflow-hidden",
        order.isOverdue && "border-destructive/30 bg-destructive/2",
      )}
    >
      <div className="flex">
        <div
          className={cn(
            "w-1.5 shrink-0",
            order.isOverdue ? "bg-destructive/70" : "bg-secondary/60",
          )}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:gap-6">
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={IMPORT_ORDER_SOURCE_CLASS[order.source]}
              >
                {IMPORT_ORDER_SOURCE_LABELS[order.source]}
              </Badge>
              <Badge className={IMPORT_ORDER_STATUS_CLASS[order.status]}>
                {IMPORT_ORDER_STATUS_LABELS[order.status]}
              </Badge>
              {order.isOverdue ? (
                <Badge variant="destructive">ເກີນກຳນົດ</Badge>
              ) : null}
              <ImportOrderPendingCountBadge count={order.pendingLineCount ?? 0} />
            </div>

            <div className="space-y-1">
              <h1 className="break-all font-display text-2xl leading-tight sm:text-3xl">
                {order.externalOrderNo}
              </h1>
              <p className="text-muted-foreground text-sm tabular-nums">
                {order.orderNumber}
              </p>
            </div>

            {productGroups.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {productGroups.map((name) => (
                  <li
                    key={name}
                    className="flex items-start gap-2 font-medium text-sm sm:text-base"
                  >
                    <Package className="mt-0.5 size-4 shrink-0 text-secondary" />
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີລາຍການສິນຄ້າ
              </p>
            )}

            <p className="text-muted-foreground text-xs">
              {productGroups.length} ກຸ່ມສິນຄ້າ · {order.lineCount} ລາຍການ ·{" "}
              {order.totalOrdered} ຊິ້ນສັ່ງ
            </p>

            {order.note ? (
              <div className="rounded-xl border border-secondary/20 bg-secondary/5 px-3 py-2.5 text-sm">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  ໝາຍເຫດ
                </p>
                <p className="mt-1 whitespace-pre-wrap leading-relaxed">
                  {order.note}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-col gap-4 lg:w-72">
            <ImportOrderReceiveProgress
              received={order.totalReceived}
              ordered={order.totalOrdered}
              size="md"
            />
            <div className="grid grid-cols-2 gap-2">
              <StatTile
                label="Landed"
                value={formatLakAmount(order.landedTotalLak)}
                accent="secondary"
              />
              <StatTile
                label="ຄ້າງຮັບ"
                value={formatLakAmount(order.inTransitValueLak)}
                accent={order.totalRemaining > 0 ? "warn" : undefined}
              />
              <StatTile
                label="ຮັບແລ້ວ"
                value={String(order.totalReceived)}
                accent="leaf"
              />
              <StatTile label="ຄ້າງ" value={String(order.totalRemaining)} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export function ImportOrderDetailFinancePanel({
  order,
}: {
  order: ImportOrderPhase13DetailDTO;
}) {
  return (
    <DetailSection title="ມູນຄ່າ & ສະກຸນເງິນ">
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <StatTile
          label="ມູນຄ່າສິນຄ້າ"
          value={formatLakAmount(order.goodsSubtotalLak)}
        />
        <StatTile
          label="ຄ່າຂົນສົ່ງ"
          value={formatLakAmount(order.shippingTotalLak)}
        />
        <MetaField
          label="ສະກຸນເງິນ"
          value={
            IMPORT_ORDER_CURRENCY_LABELS[
              order.currency === "LAK" ? "LAK" : "CNY"
            ]
          }
        />
        <MetaField
          label="ອັດຕາແລກປ່ຽນ"
          value={
            order.currency === "LAK"
              ? "ບໍ່ຄຳນວນ (ລາຄາເປັນ LAK)"
              : `${order.exchangeRate} CNY→LAK`
          }
        />
        {order.currency !== "LAK" ? (
          <MetaField
            label="ມູນຄ່າສິນຄ້າ (ຕ່າງປະເທດ)"
            value={Number(order.goodsSubtotalForeign).toLocaleString("lo-LA")}
          />
        ) : null}
      </div>
    </DetailSection>
  );
}

export function ImportOrderDetailTimelinePanel({
  order,
}: {
  order: ImportOrderPhase13DetailDTO;
}) {
  return (
    <DetailSection title="ໄລຍະເວລາ">
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <MetaField
          label="ວັນທີສັ່ງ"
          value={
            order.orderedAt ? formatDateLocal(order.orderedAt) : "—"
          }
        />
        <MetaField
          label="ຄາດວ່າຮອດ"
          value={
            order.expectedArrivalDate
              ? formatDateLocal(order.expectedArrivalDate)
              : "—"
          }
          warn={order.isOverdue}
        />
        <MetaField
          label="ສ້າງເມື່ອ"
          value={formatDateTimeLocal(order.createdAt)}
        />
        <MetaField
          label="ອັບເດດລ່າສຸດ"
          value={formatDateTimeLocal(order.updatedAt)}
        />
      </div>
    </DetailSection>
  );
}

export function ImportOrderDetailTrackingsPanel({
  trackings,
}: {
  trackings: ImportOrderTrackingRowDTO[];
}) {
  if (trackings.length === 0) return null;

  return (
    <DetailSection title="Tracking" hint={`${trackings.length} ລາຍການ`}>
      <ul className="divide-y divide-secondary/15">
        {trackings.map((tracking) => (
          <li key={tracking.id} className="px-4 py-3">
            <p className="font-medium text-sm tabular-nums">
              {tracking.trackingNo}
            </p>
            <p className="mt-0.5 text-muted-foreground text-xs">
              {tracking.carrierName ?? "—"}
              {tracking.shippedAt
                ? ` · ສົ່ງ ${formatDateLocal(tracking.shippedAt)}`
                : ""}
              {tracking.arrivedAt
                ? ` · ຮອດ ${formatDateLocal(tracking.arrivedAt)}`
                : ""}
            </p>
            {tracking.note ? (
              <p className="mt-1 text-muted-foreground text-xs">{tracking.note}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

export function ImportOrderDetailGoodsReceiptsPanel({
  receipts,
  canReceive,
  isPending,
  onOpen,
  onVoid,
}: {
  receipts: GoodsReceiptListItemDTO[];
  canReceive: boolean;
  isPending: boolean;
  onOpen: (id: string) => void;
  onVoid: (id: string) => void;
}) {
  const active = receipts.filter((receipt) => receipt.status !== "void");
  if (active.length === 0) return null;

  return (
    <DetailSection title="ໃບຮັບເຂົ້າສະຕັອກ" hint={`${active.length} ໃບ`}>
      <ul className="divide-y divide-secondary/15">
        {receipts.map((receipt) => (
          <li key={receipt.id} className="px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <button
                type="button"
                className="min-w-0 flex-1 space-y-1 text-left"
                onClick={() => onOpen(receipt.id)}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium tabular-nums">
                    {receipt.receiptNumber}
                  </span>
                  <Badge
                    className={
                      receipt.status === "void"
                        ? "border-destructive/30 bg-destructive/10 text-destructive"
                        : receipt.status === "confirmed"
                          ? "border-leaf/40 bg-leaf/10 text-leaf"
                          : "border-border bg-muted/40 text-foreground"
                    }
                  >
                    {receipt.status === "void"
                      ? "ຍົກເລີກ"
                      : receipt.status === "confirmed"
                        ? "ຢືນຢັນແລ້ວ"
                        : "ຮ່າງ"}
                  </Badge>
                </div>
                <p className="text-muted-foreground text-xs">
                  {formatDateTimeLocal(receipt.receivedAt)} · {receipt.lineCount}{" "}
                  ລາຍການ ·{" "}
                  {receipt.totalStockQuantity || receipt.totalQuantity} ຊິ້ນ
                </p>
              </button>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => onOpen(receipt.id)}
                >
                  ເປີດໃບ
                </Button>
                {canReceive && receipt.status !== "void" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full border-destructive/30 text-destructive"
                    disabled={isPending}
                    onClick={() => onVoid(receipt.id)}
                  >
                    <RotateCcw data-icon="inline-start" />
                    ຍ້ອນ
                  </Button>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

export function ImportOrderDetailLegacyReceiptsPanel({
  receipts,
  canReceive,
  isPending,
  onVoid,
}: {
  receipts: ImportOrderReceiptDTO[];
  canReceive: boolean;
  isPending: boolean;
  onVoid: (id: string) => void;
}) {
  if (receipts.length === 0) return null;

  return (
    <DetailSection title="ປະຫວັດຮັບເຂົ້າ (ເກົ່າ)" hint={`${receipts.length} ຄັ້ງ`}>
      <ul className="divide-y divide-secondary/15">
        {receipts.map((receipt) => (
          <li key={receipt.id} className="px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 space-y-1 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {formatDateTimeLocal(receipt.receivedAt)}
                  </span>
                  <Badge
                    className={
                      receipt.status === "void"
                        ? "border-destructive/30 bg-destructive/10 text-destructive"
                        : "border-leaf/40 bg-leaf/10 text-leaf"
                    }
                  >
                    {receipt.status === "void" ? "ຍ້ອນແລ້ວ" : "ຮັບແລ້ວ"}
                  </Badge>
                  <span className="text-muted-foreground tabular-nums">
                    {receipt.totalReceived} ຊິ້ນ
                  </span>
                </div>
                {receipt.note ? (
                  <p className="text-muted-foreground text-xs">{receipt.note}</p>
                ) : null}
                <ul className="text-muted-foreground text-xs">
                  {receipt.lines.map((line) => (
                    <li key={line.id}>
                      {line.productName} · {line.variantName} ×{" "}
                      {line.receivedQuantity}
                    </li>
                  ))}
                </ul>
              </div>
              {canReceive && receipt.status === "confirmed" ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-destructive/30 text-destructive"
                  disabled={isPending}
                  onClick={() => onVoid(receipt.id)}
                >
                  <RotateCcw data-icon="inline-start" />
                  ຍ້ອນ
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

export function ImportOrderDetailActionsBar({
  children,
}: {
  children: ReactNode;
}) {
  if (!children) return null;
  return (
    <div className="brand-card flex flex-wrap items-center gap-2 p-3">
      {children}
    </div>
  );
}
